import { useMemo } from 'react';
import { ShieldCheck, UserPlus, Wrench, Users, Building2, Tag, Trash2 } from 'lucide-react';

import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PageLayout } from '@/components/shared/PageLayout';
import { PageHeader } from '@/components/shared/PageHeader';
import { AdminSectionCard } from '../components/AdminSectionCard';
import { MembersTab } from '../components/MembersTab';
import { CompaniesTab } from '../components/CompaniesTab';
import { RegisterForm } from '../components/RegisterForm';
import { RegisterToolForm } from '../components/RegisterToolForm';
import { ReferenceAliasTab } from '../components/ReferenceAliasTab';
import { ToolDeletionTab } from '../components/ToolDeletionTab';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiClient } from '@/hooks/useApiClient';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import { AdminApiClient } from '@/features/admin/api/AdminApiClient';
import { ToolAliasApiClient } from '@/features/traceability/api/ToolAliasApiClient';
import { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';

export function AdminPage() {
  const { t } = useTranslation();
  const { tokenService } = useAuth();
  const http = useApiClient();
  const client = useMemo(() => (http ? new AdminApiClient(http) : null), [http]);

  const aliasClient = useMemo(() => (http ? new ToolAliasApiClient(http) : null), [http]);
  const traceabilityClient = useMemo(() => (http ? new TraceabilityApiClient(http) : null), [http]);
  const { refresh: refreshAliases } = useReferenceAlias();

  const role = tokenService?.getRole() ?? 'user';
  const isSuperAdmin = role === 'super-admin';
  const isAdmin = role === 'admin' || isSuperAdmin;
  const lockedCompany = isSuperAdmin ? undefined : (tokenService?.getCompany() ?? undefined);
  const currentUserEmail = tokenService?.getUserMail() ?? '';

  return (
    <PageLayout contentClassName="max-w-5xl">
      <PageHeader
        label={{ icon: ShieldCheck, text: t('admin.hero.badge') }}
        title={t('admin.hero.title')}
        description={t('admin.hero.description')}
      />

      <Tabs defaultValue="user" className="flex w-full flex-col gap-5">
        <TabsList className="w-fit rounded-lg bg-muted p-1">
          <TabsTrigger value="user" className="flex items-center gap-1.5 rounded-md">
            <UserPlus className="h-3.5 w-3.5" aria-hidden />
            {t('admin.tabs.users')}
          </TabsTrigger>
          <TabsTrigger value="tool" className="flex items-center gap-1.5 rounded-md">
            <Wrench className="h-3.5 w-3.5" aria-hidden />
            {t('admin.tabs.tools')}
          </TabsTrigger>
          <TabsTrigger value="aliases" className="flex items-center gap-1.5 rounded-md">
            <Tag className="h-3.5 w-3.5" aria-hidden />
            {t('referenceAliases.tabs.title')}
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="toolDeletion" className="flex items-center gap-1.5 rounded-md">
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              {t('adminTools.tab')}
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="members" className="flex items-center gap-1.5 rounded-md">
              <Users className="h-3.5 w-3.5" aria-hidden />
              {t('administration.tabs.members')}
            </TabsTrigger>
          )}
          {isSuperAdmin && (
            <TabsTrigger value="companies" className="flex items-center gap-1.5 rounded-md">
              <Building2 className="h-3.5 w-3.5" aria-hidden />
              {t('administration.tabs.companies')}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="user" className="focus-visible:outline-none">
          <AdminSectionCard
            icon={UserPlus}
            title={t('admin.sections.userCreation.title')}
            description={t('admin.sections.userCreation.description')}
          >
            <RegisterForm lockedCompany={lockedCompany} />
          </AdminSectionCard>
        </TabsContent>

        <TabsContent value="tool" className="focus-visible:outline-none">
          <AdminSectionCard
            icon={Wrench}
            title={t('admin.sections.toolCreation.title')}
            description={t('admin.sections.toolCreation.description')}
          >
            <RegisterToolForm />
          </AdminSectionCard>
        </TabsContent>

        <TabsContent value="aliases" className="focus-visible:outline-none">
          {aliasClient && traceabilityClient && (
            <ReferenceAliasTab
              client={aliasClient}
              traceabilityClient={traceabilityClient}
              adminClient={client ?? undefined}
              companyName={lockedCompany}
              isSuperAdmin={isSuperAdmin}
              onAliasChange={refreshAliases}
            />
          )}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="toolDeletion" className="focus-visible:outline-none">
            <AdminSectionCard
              icon={Trash2}
              title={t('adminTools.title')}
              description={t('adminTools.subtitle')}
            >
              <ToolDeletionTab />
            </AdminSectionCard>
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="members" className="focus-visible:outline-none">
            {client && <MembersTab client={client} currentUserEmail={currentUserEmail} />}
          </TabsContent>
        )}
        {isSuperAdmin && (
          <TabsContent value="companies" className="focus-visible:outline-none">
            {client && <CompaniesTab client={client} />}
          </TabsContent>
        )}
      </Tabs>
    </PageLayout>
  );
}
